import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import SelectInput from '@/components/General/inputs/SelectInput';
import Input from '@/components/General/inputs/Input';
import { isBinding } from '@/utils/automationBuilder';

const SOURCE_GROUP_LABELS = {
    trigger: 'Trigger',
    input: 'Inputs',
    run: 'Run',
    step: 'Step outputs',
};

/**
 * AutomationBindingInput — the v2 context-sensitive variable picker (§14.5).
 *
 * Offers only the variables the caller filtered in (semantic/type-aware):
 * trigger.<ns>.*, input.*, run.*, and guaranteed-upstream
 * steps.<id>.output.* — never person.* under GENERIC mode. Selecting a
 * variable stores { var: key }; optional manual entry stores a literal.
 *
 * Props:
 *  - variables: [{key,label,type?,semantic?,source?}] (already filtered)
 *  - paramSemantic: semantic label shown in the hint (e.g. 'person ID')
 *  - value: current value — {var:key} binding or literal
 *  - onUpdate
 *  - allowManualEntry: show an "enter a value" toggle for literals
 *  - title, placeholder, helpText, required, disabled, testID
 */
const AutomationBindingInput = ({
    variables = [],
    value,
    onUpdate,
    title,
    placeholder,
    helpText,
    required,
    disabled,
    allowManualEntry = true,
    testID,
}) => {
    const bindingActive = isBinding(value);
    // Manual mode is implied when a non-empty literal is set with no binding.
    const [manualMode, setManualMode] = useState(false);

    const varByKey = useMemo(() => {
        const map = {};
        for (const v of variables) map[v.key] = v;
        return map;
    }, [variables]);

    const options = useMemo(() => {
        const grouped = {};
        for (const v of variables) {
            const group = v.source || 'trigger';
            (grouped[group] = grouped[group] || []).push(v);
        }
        const opts = [];
        let firstGroup = true;
        for (const source of ['trigger', 'input', 'run', 'step']) {
            const vars = grouped[source];
            if (!vars?.length) continue;
            if (!firstGroup) opts.push({ separator: true });
            firstGroup = false;
            for (const v of vars) {
                opts.push({
                    value: { var: v.key },
                    label: `${SOURCE_GROUP_LABELS[source]}: ${v.label}`,
                    icon: { web: 'flash' },
                    testID: `variable-option-${v.key}`,
                });
            }
        }
        return opts;
    }, [variables]);

    const staleBindingKey = bindingActive && !varByKey[value.var] ? value.var : null;

    const displayValue = useMemo(() => {
        if (!bindingActive) return undefined;
        const spec = varByKey[value.var];
        const sourceLabel = spec ? (SOURCE_GROUP_LABELS[spec.source] || 'Variable') : 'Variable';
        return { label: `${sourceLabel}: ${spec?.label || value.var}`, value };
    }, [bindingActive, varByKey, value]);

    const showManual = allowManualEntry && (manualMode || (!bindingActive && value !== undefined && value !== null && value !== ''));

    return (
        <View>
            {!showManual && (
                <SelectInput
                    title={title}
                    placeholder={placeholder || 'Bind a variable...'}
                    helpText={helpText}
                    options={options}
                    value={displayValue}
                    onUpdate={onUpdate}
                    disabled={disabled}
                    required={required}
                />
            )}
            {showManual && (
                <Input
                    title={title}
                    placeholder={placeholder || 'Enter a value...'}
                    value={bindingActive ? '' : (value ?? '')}
                    onUpdate={onUpdate}
                    helpText={helpText}
                />
            )}
            {staleBindingKey && (
                <Text style={styles.staleWarning} testID={`${testID || title}-stale-warning`}>
                    Variable no longer available: {staleBindingKey}
                </Text>
            )}
            {allowManualEntry && !disabled && (
                <TouchableOpacity
                    onPress={() => {
                        setManualMode(!showManual);
                        onUpdate(showManual ? undefined : '');
                    }}
                    style={styles.modeToggle}
                    hitSlop={8}
                    testID={`${testID || title}-manual-toggle`}
                >
                    <Text style={styles.modeToggleText}>
                        {showManual ? 'Bind a variable instead' : 'Enter a value instead'}
                    </Text>
                </TouchableOpacity>
            )}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    staleWarning: {
        fontSize: 12,
        color: colors.error,
        fontStyle: 'italic',
        marginTop: 4,
        marginBottom: 8,
    },
    modeToggle: { alignSelf: 'flex-start', marginTop: -4, marginBottom: 8 },
    modeToggleText: { fontSize: 12, fontFamily: fonts.bold, color: colors.primary },
}));

export default AutomationBindingInput;
